import React, { useState, useEffect } from 'react';
import PropTypes from "prop-types";
import TableViewHook from "../../containers/TableViewHook";
import TableGridHook from "../../containers/TableGridHook";
import { useDispatch, useSelector } from "react-redux";
import CardContent from "@mui/material/CardContent";
import Card from "@mui/material/Card";
import { getTemplateList, updatePageAndGetList, updateSortAndGetList } from "../../actions/templateListActions";
import { openDrawer } from "../../actions/drawerActions";
import ActionReviewDrawerHook, { actionReviewDrawerProps } from "../drawers/ActionReviewDrawerHook";
import ActionCreateEditDrawerHook, { actionCreateEditDrawerProps } from "../drawers/ActionCreateEditDrawerHook";
import ArgoScreenSizePopUpHook from "../common/ArgoScreenSizePopUpHook";
import FilterPopOverHook from "../dialogs/filters/FilterPopOverHook";
import ArgoTemplateListCreateFabHook from "../common/ArgoTemplateListCreateFabHook";
import ArgoListTitleAndLoadingAnimationHook from "../common/ArgoListTitleAndLoadingAnimationHook";
import formatCopyText from "../../utilities/formatCopyText";
import { QD_URL } from "../../constants/systemConstants";
import * as SYSTEM from "../../constants/systemConstants";
import * as metadataTemplateTypes from "../../constants/metadataTemplateTypes";
import * as TEMPLATE from "../../constants/templateConstants";

const ActionListHook = (props) => {
    const { match } = props;

    const [state, setState] = useState({
        loading: false,
        filter: false,
        createMode: false,
        closePopUp: false
    });

    const dispatch = useDispatch();
    const actionTemplateList = useSelector(state => state.actionTemplateList);
    const filter = useSelector(state => state.filter[TEMPLATE.FILTER.current.action] || {});

    useEffect(() => {
        setState(prevState => ({ ...prevState, loading: true }));
        dispatch(getTemplateList(filter, TEMPLATE.TYPE.ACTION, TEMPLATE.MODE.CURRENT));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    useEffect(() => {
        if (state.loading) {
            if (SYSTEM.USE_TIMER_DELAY) {
                // NOTE: Only use for local testing of loading render animation, before using for PRODUCTION set SYSTEM.USE_TIMER_DELAY_AMOUNT = false in systemConstants.js
                setTimeout(delayedSetLoadingFalse, SYSTEM.USE_TIMER_DELAY_AMOUNT); // 1000ms = 1 second, // Used to test loading animation rendering
            } else {
                setState(prevState => ({ ...prevState, loading: false, filter: false, createMode: false }));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [actionTemplateList]);

    // Used to test loading animation rendering
    const delayedSetLoadingFalse = () => {
        setState(prevState => ({ ...prevState, loading: false }));
    }

    const setLoadingStateTrue = (callback, parm1 = null) => {
        // If a parameter is passed then pass it to call back, should be the template of the item to be saved edit or created
        if (parm1 === null) {
            setState(prevState => ({ ...prevState, loading: true, filter: true }));
            callback();
        } else {
            setState(prevState => ({ ...prevState, loading: true, filter: true }));
            callback(parm1);
        }
    }

    const openEdit = (templateKey, id, templateMode) => {
        if (!templateKey && match && match.params) {
            templateKey = match.params.templateKey;
        }

        dispatch(openDrawer(
            actionCreateEditDrawerProps, 
            <ActionCreateEditDrawerHook templateKey={templateKey} templateId={id} filter={filter} templateMode={templateMode} />
        ));
    };

    const openCreate = () => {
        dispatch(openDrawer(
            actionCreateEditDrawerProps, 
            <ActionCreateEditDrawerHook createMode={true} />
        ));
    };

    const openActionReviewDrawer = (templateKey, id, name, row, event) => {
        if (!templateKey && match && match.params) {
            templateKey = match.params.templateKey;
        }
        formatCopyText(row, event, "Action:");
        dispatch(openDrawer(
            actionReviewDrawerProps, 
            <ActionReviewDrawerHook templateKey={templateKey} templateId={id} />
        ));
    };

    const onPageChange = (page, size, event) => {
        setState(prevState => ({ ...prevState, loading: true, filter: true }));
        dispatch(updatePageAndGetList(page, size, filter, TEMPLATE.TYPE.ACTION, TEMPLATE.MODE.CURRENT, event));
    };

    const onSortChange = (sort, direction) => {
        setState(prevState => ({ ...prevState, loading: true, filter: true }));
        dispatch(updateSortAndGetList(sort, direction, filter, TEMPLATE.TYPE.ACTION, TEMPLATE.MODE.CURRENT));
    };

    const handleClosePopUp = () => {
        localStorage.setItem("hasSeenWelcomeMessage", "true");
        setState(prevState => ({ ...prevState, closePopUp: true }));
    };

    // Custom actions for the table grid
    const customActions = [
        {
            func: openActionReviewDrawer,
            label: 'Review'
        },
        {
            func: openEdit,
            label: "Edit"
        }
    ];

    // Used by the versions list and stored in the version filters
    const defaultActions = {
        edit: openEdit,
        create: openCreate,
        review: openActionReviewDrawer
    };

    return (
        <div id="ActionList-container-div" style={{height: "100%", padding: "15px", marginTop: '64px'}}>
            {state.closePopUp ? "" : <ArgoScreenSizePopUpHook closeFunction={handleClosePopUp} />}

            <ArgoListTitleAndLoadingAnimationHook fileName="ActionList" listTitle="Actions" loading={state.loading} showOutstandingAlert={false} filter={state.filter} />

            <div style={{clear: "both"}}></div>

            <Card id="ActionList-card"
                  variant="outlined"
                  sx={{border: "1px solid lightgray", height: "100%", boxShadow: "2px 4px whitesmoke"
                  }}>
                <CardContent id="ActionList-card-content">

                    <TableViewHook
                        id="ActionList-table-view"
                        templateType={TEMPLATE.TYPE.ACTION}
                        templateMode={TEMPLATE.MODE.CURRENT}
                        setLoadingState={setLoadingStateTrue}
                        filter={<FilterPopOverHook page="actions"
                                               templateType={TEMPLATE.TYPE.ACTION}
                                               templateMode={TEMPLATE.MODE.CURRENT}
                                               filterName={TEMPLATE.FILTER.current.action}
                                               submitValue={getTemplateList}
                                               statuses={["All", "Draft", "Published"]}
                                               showActionType={true}
                                               types={{INTERVENTION: 'Intervention', GOAL: 'Goal'}}
                                               showQualifier={false}
                                               setLoadingState={setLoadingStateTrue} parentName={"ActionList"}
                        />}
                    >
                        <TableGridHook
                            id="ActionList-table-grid"
                            displayType={"actions"}
                            dataSource={actionTemplateList}
                            nameDrawer={openActionReviewDrawer}
                            link={QD_URL +"/action-editor/"}
                            customActions={customActions}
                            pageState={filter}
                            onPageChange={onPageChange}
                            sortState={filter}
                            onSortChange={onSortChange}
                            type={metadataTemplateTypes.AnswerTemplate}
                            templateType={TEMPLATE.TYPE.ACTION}
                            templateMode={TEMPLATE.MODE.CURRENT}
                            defaultActions={defaultActions}
                            showQualifier={false}
                            showType={true}
                        />
                    </TableViewHook>

                    <ArgoTemplateListCreateFabHook
                        fileName="ActionList"
                        fieldName="fab"
                        hoverText="Create Action"
                        onClick={openCreate}
                    />

                </CardContent>

            </Card>

        </div>
    );
};

ActionListHook.propTypes = {
    match: PropTypes.object,
    location: PropTypes.object
};

export default ActionListHook;
